import { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { GavetaDeNotificacoes } from '@/modulos/operacao/analises/GavetaDeNotificacoes';
import { useAuth } from '@/nucleo/auth/useAuth';
import { SkipToContent } from '@/ui/SkipToContent';
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/ui/base/sidebar';
import { ThemeToggle } from '@/ui/ThemeToggle';

import { BarraLateral } from './BarraLateral';
import { BuscaGlobal } from './BuscaGlobal';
import { grupoDoItem, itemPorPrefixoDeCaminho, type AreaDeNavegacao } from './mapa';
import { MenuDaConta } from './MenuDaConta';
import { Migalhas } from './Migalhas';
import { ProvedorDeMigalhaExtra } from './MigalhaExtra';

/**
 * Casca das áreas internas: /operacao, /admin, /sme e /dre.
 *
 * Barra lateral gerada do mapa de navegação, cabeçalho com o botão de recolher, o
 * alternador de tema e o menu da conta, faixa de breadcrumb (também derivada do
 * mapa) e o conteúdo em `<main id="conteudo">`, destino do "pular para o
 * conteúdo". O título da tela não fica mais solto no cabeçalho: quem diz "onde
 * estou" agora é só o breadcrumb, sem repetir a mesma informação duas vezes.
 *
 * A estrutura reaproveita o arranjo do protótipo, reconstruída sobre o que existe
 * aqui: o `useAuth` do Prompt 5 no lugar do contexto de autenticação de lá, e sem
 * nada do que era só do protótipo: troca de persona, modo de apresentação e
 * painel de homologação não fazem parte deste sistema.
 */
export function CascaDeArea({ area }: { area: AreaDeNavegacao }) {
  const { usuario, sair } = useAuth();
  const { pathname } = useLocation();
  const [migalhaExtra, setMigalhaExtra] = useState<string | null>(null);

  const item = itemPorPrefixoDeCaminho(pathname);
  const grupo = item ? grupoDoItem(item.caminho) : undefined;

  return (
    <SidebarProvider>
      <SkipToContent />
      <BarraLateral area={area} />

      {/*
        `role="group"`: `SidebarInset` renderiza `<main>` por padrão (uso comum
        do shadcn), mas aqui dentro já existe o `<main id="conteudo">` de
        verdade, alvo do "pular para o conteúdo". Dois `<main>` aninhados são
        dois marcos de conteúdo principal sem nome para quem navega por
        landmark — a sobreposição não conta como erro de build nem de teste,
        só aparece auditando função (o "Duas lições" de CLAUDE.md: testar, não
        prever). O papel de landmark fica só no de dentro.
      */}
      <SidebarInset role="group">
        <header className="sticky top-0 z-10 flex shrink-0 flex-col border-b border-border bg-card/80 backdrop-blur-xs">
          <div className="flex h-14 items-center gap-2 px-3 sm:px-4">
            <SidebarTrigger className="-ml-1" />

            <BuscaGlobal area={area} />

            {/*
              O sino voltou ao cabeçalho, agora com o que a regra 7 do CLAUDE.md
              exige dele: contagem de não lidas e uma gaveta que abre, lista,
              leva à origem do aviso e dá acesso à tela cheia. Ele havia saído
              justamente por ser um botão sem `onClick` e sem contagem.
            */}
            <GavetaDeNotificacoes />

            <ThemeToggle />

            {usuario && (
              <MenuDaConta usuario={usuario} areaAtual={area} aoSair={() => void sair()} />
            )}
          </div>

          <Migalhas raizDaArea={area.raiz} grupo={grupo} item={item} extra={migalhaExtra} />
        </header>

        {/*
          min-w-0 nos dois: sem isto, um filho mais largo que a viewport (ex.:
          uma tabela larga) empurra a pagina inteira em vez de so rolar por
          dentro de si mesmo (mesma armadilha de min-width:auto corrigida em
          ui/base/sidebar.tsx e ui/base/chart.tsx; achado real em
          operacional/inscricoes/InscricoesListaPage.tsx).
        */}
        <main id="conteudo" className="flex min-w-0 flex-1 flex-col px-4 py-6 sm:px-6">
          <div className="mx-auto flex w-full min-w-0 max-w-[1400px] flex-1 flex-col space-y-6">
            <ProvedorDeMigalhaExtra definir={setMigalhaExtra}>
              <Outlet />
            </ProvedorDeMigalhaExtra>
          </div>
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}
